// @flow
'use strict'

const get_ = require('lodash/get')
const flow_ = require('lodash/flow')
const maybe = require('folktale/maybe')
const result = require('folktale/result')

const queryResults = require('../helpers/queryResults')
const { getFieldReferencedCollection } = require('../schemas/helpers')

const { isSameRecord } = require('./records')
const recordStoreServiceCreator = require('./service')

/*::
import type { RavenProxy } from '../raven/types'
import type { RecordStore, RecordStoreService } from './types'
import type { Tracer } from '../tracing/tracer'

export type CreateRecordStoreServiceArgs = {|
  wixDataProxy: WixDataApi,
  controllerConfig: { dataset: { collectionName: string } },
  datasetType: string,
  Raven: RavenProxy,
  tracer: Tracer
|}

type CreateRecordStoreInstanceArgs = {|
  recordStoreService: Result<string, RecordStoreService>,
  getConfig: () => {| sort: {}, filter: {} |},
  getPageSize: () => number,
  prefetchedData: WixDataQueryResult,
  datasetId: string,
  filterResolver: ({}) => Maybe<{}>,
  getSchema: (schemaName?: string) => ?WixDataSchema
|}
*/

const createRecordStoreService = (
  {
    wixDataProxy,
    controllerConfig,
    datasetType,
    Raven,
    tracer
  } /*: CreateRecordStoreServiceArgs */
) => {
  const datasetConfig = result
    .fromNullable(controllerConfig, 'missing controller configuration')
    .chain(({ dataset }) =>
      result.fromNullable(
        dataset,
        'controller configuration is missing dataset object'
      )
    )
  const collectionName = datasetConfig.chain(({ collectionName }) =>
    result.fromNullable(
      collectionName,
      'dataset is not connected to a collection'
    )
  )

  return collectionName.map(mainCollectionName => {
    const includes = get_(controllerConfig, ['dataset', 'includes'])
    const readWriteType = get_(controllerConfig, ['dataset', 'readWriteType'])

    return recordStoreServiceCreator({
      wixDataProxy,
      mainCollectionName,
      includes,
      readWriteType,
      datasetType,
      Raven,
      tracer
    })
  })
}

const createRecordStoreInstance = (
  {
    recordStoreService,
    getConfig,
    getPageSize,
    prefetchedData,
    datasetId,
    filterResolver,
    getSchema
  } /*: CreateRecordStoreInstanceArgs */
) /*: RecordStore */ => byRefField => {
  const config = getConfig()
  const pageSize = getPageSize()
  const sort = config.sort

  return recordStoreService.chain(serviceCreator => {
    if (byRefField) {
      return result
        .fromNullable(
          getFieldReferencedCollection(byRefField, getSchema()),
          `cannot resolve referenced collection name for field ${byRefField}`
        )
        .map(referencedCollectionName => {
          return serviceCreator({
            pageSize,
            sort: null,
            filter: null,
            seed: maybe.Nothing(),
            datasetId,
            referencedCollectionName
          })
        })
    } else {
      return result.fromMaybe(
        filterResolver(config.filter).map(filter =>
          serviceCreator({
            pageSize,
            sort,
            filter,
            seed: queryResults
              .fromWixDataQueryResults(prefetchedData, 0)
              .matchWith({
                Empty: maybe.Nothing,
                Results: flow_(queryResults.of, maybe.Just)
              }),
            datasetId,
            referencedCollectionName: null
          })
        ),
        'could not resolve dynamic filter'
      )
    }
  })
}

module.exports = {
  isSameRecord,
  createRecordStoreService,
  createRecordStoreInstance
}



// WEBPACK FOOTER //
// ./record-store/index.js